import PouchDB from "pouchdb";
import PouchDBUpsert from "pouchdb-upsert";
import PouchDBFind from "pouchdb-find";
import { type PotaParkType } from "@/types";
import { potaParksLogger } from "@/utils/loggers";
import { normalizeTimestamp } from "@/utils/dateAndTime";
import HamrsApiService from "@/services/HamrsApiService";
import PotaParkJsonRepo from "@/repos/PotaParkJsonRepo";
import { usePotaParksInitStore } from "@/stores/PotaParksInitStore";
import { useOnline } from '@vueuse/core';

// Add PouchDB plugins
PouchDB.plugin(PouchDBUpsert);
PouchDB.plugin(PouchDBFind);

// Initialize online status at module level
const online = useOnline();

export type PotaParkDbMetaData = {
  _id: string;
  lastUpdated: number;
  totalParks: number;
  isInitialized: boolean;
};

// Create PouchDB instance
const potaParkDb = new PouchDB("pota-parks.db");
const METADATA_DOC_ID = "PARKS_METADATA";
const UPDATE_INTERVAL_HOURS = 24; // Check for updates daily

/**
 * Helper function to normalize a park object for database storage
 * @param park The park object to normalize
 * @returns A new park object with normalized timestamp and uppercase location
 */
function toDbPark(park: PotaParkType): PotaParkType & { _id: string } {
  return {
    ...park,
    _id: park.reference,
    updatedAt: normalizeTimestamp(park.updatedAt),
    location: park.location?.toUpperCase() ?? ''
  };
}

const PotaParkPouchRepo = {
  async initializeParksData(): Promise<void> {
    const initStore = usePotaParksInitStore();

    try {
      const metadata = await this.getMetadata();

      // If not initialized, bootstrap from static data first (with UI)
      if (!metadata.isInitialized) {
        initStore.startInitialization();
        potaParksLogger("Bootstrapping from static data first");
        await this.initializeFromStaticData();
        // After static data is loaded, try to sync with API for updates (invisible)
        this.syncUpdatesInBackground();
        return;
      }

      // If already initialized, no UI needed - just check for background updates
      const shouldUpdate = this.shouldUpdateParks(metadata);

      if (shouldUpdate && online.value) {
        potaParksLogger("Checking for park data updates in background");
        // Background sync without UI
        await this.syncParksFromServer(metadata, false);
      } else if (shouldUpdate && !online.value) {
        potaParksLogger("Updates available but device is offline");
      } else {
        potaParksLogger("Parks data is up to date");
      }
    } catch (error) {
      potaParksLogger("Error initializing parks data, falling back to static data", error);
      initStore.startInitialization();
      await this.initializeFromStaticData();
    }
  },

  // Background sync for updates after initial bootstrap (only on app start)
  syncUpdatesInBackground(): void {
    // Don't await this - let it run in background
    setTimeout(async () => {
      if (!online.value) {
        potaParksLogger("Skipping background sync - device is offline");
        return;
      }

      try {
        potaParksLogger("Starting one-time background sync for park updates");
        const metadata = await this.getMetadata();
        // showProgress = false for background sync
        await this.syncParksFromServer(metadata, false);
      } catch (error) {
        potaParksLogger("Background sync failed - user can manually refresh later", error);
      }
    }, 5000); // Wait 5 seconds before starting background sync
  },

  async getMetadata(): Promise<PotaParkDbMetaData> {
    try {
      return await potaParkDb.get<PotaParkDbMetaData>(METADATA_DOC_ID);
    } catch (error: any) {
      if (error.status === 404) {
        // No metadata exists, return default
        return {
          _id: METADATA_DOC_ID,
          lastUpdated: 0,
          totalParks: 0,
          isInitialized: false
        };
      }
      throw error;
    }
  },

  async updateMetadata(updates: Partial<PotaParkDbMetaData>): Promise<void> {
    try {
      await potaParkDb.upsert(METADATA_DOC_ID, (doc: any) => ({
        _id: METADATA_DOC_ID,
        lastUpdated: 0,
        totalParks: 0,
        isInitialized: false,
        ...doc,
        ...updates
      }));
    } catch (error) {
      potaParksLogger("Error updating metadata", error);
      throw error;
    }
  },

  shouldUpdateParks(metadata: PotaParkDbMetaData): boolean {
    if (!metadata.isInitialized) {
      return true;
    }

    const hoursSinceUpdate = (Date.now() - metadata.lastUpdated) / (1000 * 60 * 60);
    return hoursSinceUpdate >= UPDATE_INTERVAL_HOURS;
  },

  async syncParksFromServer(metadata: PotaParkDbMetaData, showProgress: boolean = true): Promise<void> {
    const initStore = usePotaParksInitStore();

    try {
      // Use appropriate timestamp based on initialization status:
      // 1. For initial sync (not initialized), use the static data timestamp as baseline
      // 2. For subsequent syncs (initialized), use metadata.lastUpdated from our last successful sync
      const rawStaticTimestamp = PotaParkJsonRepo.lastUpdatedAt();
      if (!rawStaticTimestamp) {
        potaParksLogger('Static data timestamp invalid, defaulting to metadata.lastUpdated');
      }

      // Normalize static data timestamp to ensure consistent millisecond precision
      const staticDataTimestamp = rawStaticTimestamp ? normalizeTimestamp(rawStaticTimestamp) : 0;
      const sinceTimestamp = metadata.isInitialized
        ? metadata.lastUpdated  // Use last successful sync timestamp for subsequent updates
        : (staticDataTimestamp || metadata.lastUpdated);  // Use static data timestamp only for initial sync

      // Debug logging to help identify the timestamp issue
      potaParksLogger(`Debug - metadata.lastUpdated: ${new Date(metadata.lastUpdated).toISOString()}`);
      potaParksLogger(`Debug - staticDataTimestamp: ${new Date(staticDataTimestamp).toISOString()}`);
      potaParksLogger(`Debug - using sinceTimestamp: ${new Date(sinceTimestamp).toISOString()}`);

      if (showProgress) {
        initStore.setState('downloading');
      }
      potaParksLogger(`Fetching parks updated since: ${new Date(sinceTimestamp).toISOString()}`);

      const parks = await HamrsApiService.fetchParks(sinceTimestamp);

      if (parks.length > 0) {
        potaParksLogger(`Syncing ${parks.length} parks to local database`);
        if (showProgress) {
          initStore.setState('processing');
          initStore.setProgress(0, parks.length);
        }

        // Batch upsert all parks with progress reporting
        const batchSize = 50; // Smaller batch size for better progress updates
        let processedCount = 0;

        for (let i = 0; i < parks.length; i += batchSize) {
          const batch = parks.slice(i, i + batchSize);

          // Process batch
          await Promise.all(
            batch.map(park =>
              potaParkDb.upsert(park.reference, () => toDbPark(park))
            )
          );

          processedCount += batch.length;
          if (showProgress) {
            initStore.setProgress(processedCount, parks.length);
          }

          // Yield control to prevent blocking the UI
          await new Promise(resolve => setTimeout(resolve, 0));
        }

        // Update metadata
        await this.updateMetadata({
          lastUpdated: Date.now(),
          totalParks: await this.getCount(),
          isInitialized: true
        });

        potaParksLogger(`Successfully synced ${parks.length} parks`);
        if (showProgress) {
          initStore.setCompleted();
        }
      } else {
        potaParksLogger("No new parks to sync");
        // Still update the lastUpdated timestamp
        await this.updateMetadata({ lastUpdated: Date.now() });
        if (showProgress) {
          initStore.setCompleted();
        }
      }
    } catch (error) {
      potaParksLogger("Error syncing parks from server", error);

      // Only show error in UI if this is a foreground sync
      if (showProgress) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        initStore.setError(errorMessage);
      }

      throw error;
    }
  },

  async initializeFromStaticData(): Promise<void> {
    const initStore = usePotaParksInitStore();

    try {
      if (!window.parksData) {
        throw new Error("Static parks data not available");
      }

      const metadata = await this.getMetadata();
      if (metadata.isInitialized) {
        potaParksLogger("Database already initialized, skipping static data import");
        initStore.setCompleted();
        return;
      }

      potaParksLogger("Initializing from static parks data");
      const parks = window.parksData!;

      initStore.setState('processing');
      initStore.setProgress(0, parks.length);

      // Batch import static data with progress reporting
      const batchSize = 50;
      let processedCount = 0;

      for (let i = 0; i < parks.length; i += batchSize) {
        const batch = parks.slice(i, i + batchSize);

        await Promise.all(
          batch.map(park =>
            potaParkDb.upsert(park.reference, () => toDbPark(park))
          )
        );

        processedCount += batch.length;
        initStore.setProgress(processedCount, parks.length);

        // Yield control to prevent blocking the UI
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      // When initializing from static data, use its timestamp as our starting point
      // This ensures we'll only request updates that happened after the static data was generated
      const staticDataTimestamp = normalizeTimestamp(PotaParkJsonRepo.lastUpdatedAt());
      await this.updateMetadata({
        lastUpdated: staticDataTimestamp,
        totalParks: parks.length,
        isInitialized: true
      });

      potaParksLogger(`Initialized database with ${parks.length} parks from static data`);
      initStore.setCompleted();
    } catch (error) {
      potaParksLogger("Error initializing from static data", error);
      initStore.setError(error instanceof Error ? error.message : 'Failed to initialize from static data');
      throw error;
    }
  },

  async getCount(): Promise<number> {
    try {
      const result = await potaParkDb.allDocs({
        include_docs: false
      });

      // Filter out the metadata document from the count
      const parkCount = result.rows.filter(row => row.id !== METADATA_DOC_ID).length;
      return parkCount;
    } catch (error) {
      console.error("Error getting count", error);
      throw error;
    }
  },

  async findById(parkId: string): Promise<PotaParkType | null> {
    try {
      const park = await potaParkDb.get<PotaParkType>(parkId.toUpperCase());
      return park;
    } catch (error: any) {
      if (error.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async findByLocation(location: string): Promise<PotaParkType[]> {
    try {
      // Create index on location field for efficient searching
      await potaParkDb.createIndex({
        index: { fields: ['location'] }
      });

      const result = await potaParkDb.find({
        selector: { location: location.toUpperCase() },
        limit: 1000
      });

      return result.docs as any as PotaParkType[];
    } catch (error) {
      potaParksLogger("Error finding parks by location", error);
      throw error;
    }
  },

  async findNearby(latitude: number, longitude: number, radiusKm: number = 50): Promise<PotaParkType[]> {
    try {
      // Create index if it doesn't exist
      await potaParkDb.createIndex({
        index: { fields: ['latitude', 'longitude'] }
      });

      // Simple bounding box search (more sophisticated geo queries would need additional libraries)
      const latDelta = radiusKm / 111; // Rough conversion: 1 degree lat ≈ 111km
      const lonDelta = radiusKm / (111 * Math.cos(latitude * Math.PI / 180));

      const result = await potaParkDb.find({
        selector: {
          latitude: {
            $gte: latitude - latDelta,
            $lte: latitude + latDelta
          },
          longitude: {
            $gte: longitude - lonDelta,
            $lte: longitude + lonDelta
          },
          active: true
        },
        limit: 100
      });

      return result.docs as any as PotaParkType[];
    } catch (error) {
      potaParksLogger("Error finding nearby parks", error);
      throw error;
    }
  },

  async search(query: string, limit: number = 50): Promise<PotaParkType[]> {
    try {
      // Create index if it doesn't exist
      await potaParkDb.createIndex({
        index: { fields: ['name', 'id'] }
      });

      const upperQuery = query.toUpperCase();

      const result = await potaParkDb.find({
        selector: {
          $or: [
            { name: { $regex: `.*${upperQuery}.*` } },
            { id: { $regex: `.*${upperQuery}.*` } }
          ]
        },
        limit
      });

      return result.docs as any as PotaParkType[];
    } catch (error) {
      potaParksLogger("Error searching parks", error);
      throw error;
    }
  },

  async create(park: PotaParkType): Promise<PotaParkType> {
    try {
      const normalizedPark = toDbPark(park);
      await potaParkDb.upsert(park.reference, () => normalizedPark);
      potaParksLogger("Park Created", park.id);
      return normalizedPark;
    } catch (error) {
      console.error("Error creating park", error);
      throw error;
    }
  },

  async checkForUpdates(): Promise<void> {
    if (!online.value) {
      potaParksLogger("Cannot check for updates - device is offline");
      throw new Error("Device is offline");
    }

    potaParksLogger("Checking for park updates...");
    const metadata = await this.getMetadata();
    await this.syncParksFromServer(metadata); // Use existing timestamp
  },

  async forceSync(): Promise<void> {
    if (!online.value) {
      potaParksLogger("Cannot force sync - device is offline");
      throw new Error("Device is offline");
    }

    potaParksLogger("Force syncing parks using earliest available timestamp...");
    const metadata = await this.getMetadata();
    // Use the earliest timestamp we have available to avoid missing any updates
    const staticDataTimestamp = normalizeTimestamp(PotaParkJsonRepo.lastUpdatedAt());
    const earliestTimestamp = metadata.isInitialized ?
      Math.min(metadata.lastUpdated, staticDataTimestamp) :
      staticDataTimestamp;
    await this.syncParksFromServer({ ...metadata, lastUpdated: earliestTimestamp });
  }
};

export default PotaParkPouchRepo;